// Pure editing helpers for the SI (boiler) heating weekly schedule. These wrap
// darwin_core's schedule helpers so the React screen stays declarative and the
// tricky bits (which darwin_core function, the isHpi flag, cloning before an
// in-place sort, turning {min,max} ranges into selectable options) are unit
// tested in isolation.
//
// The working schedule here is the FLAT representation darwin_core's editors use:
//   [{ day, startTime, setpoint }, ...]   (startTime = minutes since midnight)
// SI heating events are contiguous setpoint segments: an event's setpoint applies
// until the next event starts, so "adding" an event really moves the following
// segment's start to the new event's endTime — darwin_core owns that arithmetic.
//
// Several darwin_core helpers (getEventsForDay, the *StartTimeRanges builders that
// call it) sort their input array IN PLACE. The working schedule we pass in can be
// the store's own draft array reference, so every wrapper clones before delegating
// to avoid mutating Redux state outside a reducer.

import {
  addEvent,
  editEvent,
  editPairedEvent as coreEditPairedEvent,
  getEventsForDay,
  defaultEndTime as coreDefaultEndTime,
  availableStartTimeRanges,
  availableEndTimeRanges,
  availableEndTimeRangesAddHaloHeating,
  editStartTimeRange,
  editEndTimeRange,
  minimumComfortTime,
  maxiumumComfortTime,
  minimumReducedTime,
  maximumReducedTime,
  areSelectionsValid,
  prepareDeletedSchedule,
  flattenSchedule,
} from 'darwin_core/schedule/schedule.js';
import { canAdd, canDelete } from 'darwin_core/schedule/validation.js';
import { copySchedule } from 'darwin_core/schedule/scheduleCopy.js';

import {
  formatEventTime,
  formatClock12,
  WEEK_DAYS,
} from '../dashboard/formatSchedule.js';

export { WEEK_DAYS };

// SI (classic/boiler) steps in 15-minute slots; HPI (heat pump) in 10.
export const SI_STEP = 15;
export const HPI_STEP = 10;

// Which of the four editing models a zone uses, from its family + type.
//   siHeating  — single events, free setpoint          (contiguous segments)
//   siDhw      — single events, on/off                 (contiguous segments)
//   hpiHeating — PAIRED comfort/reduced events         (Comfort period → Setback)
//   hpiDhw     — PAIRED on/off events                  (On period → Off)
export function getScheduleKind(isDhw, isHpi) {
  if (!isHpi) {
    return isDhw ? 'siDhw' : 'siHeating';
  }
  return isDhw ? 'hpiDhw' : 'hpiHeating';
}

export const isPairedKind = (kind) =>
  kind === 'hpiHeating' || kind === 'hpiDhw';
// The setpoint picker snaps to 0.5 °C (50 centidegrees), matching the app.
export const SETPOINT_STEP = 50;
export const DEFAULT_SETPOINT = 2000;

// Some frames deliver an SI setpoint as a numeric STRING ("1850"). The read-only
// grid coerces it for display, but the editor keyed off `typeof === 'number'` and
// silently fell back to 20 °C on save — so normalise to a number here, at the flat
// model boundary. Named HPI levels ('comfort'/'reduced') and on/off events are
// left untouched (Number('comfort') is NaN, so the numeric guard skips them).
function normalizeEvent(event) {
  const raw = event.setpoint;
  if (
    typeof raw === 'string' &&
    raw.trim() !== '' &&
    Number.isFinite(Number(raw))
  ) {
    return { ...event, setpoint: Number(raw) };
  }
  return event;
}

// A reported nested schedule ([{day, events:[...]}]) flattened to the editor's
// flat shape, tolerant of a missing/partial frame (returns [] rather than throw,
// so a zone with no schedule degrades to an empty state instead of crashing).
export function flattenReported(rawSchedule) {
  if (!Array.isArray(rawSchedule) || rawSchedule.length === 0) {
    return [];
  }
  try {
    return flattenSchedule(rawSchedule).map(normalizeEvent);
  } catch {
    return [];
  }
}

// Render a minutes-since-midnight value as the zone's preferred clock.
export function formatTime(minutes, is24Hour) {
  return is24Hour ? formatEventTime(minutes) : formatClock12(minutes);
}

// Turn darwin_core's [{min,max}] allowed-time ranges into concrete <select>
// options, one per `step`-minute slot within each (inclusive) range. `min` is
// aligned up to the next step boundary so options always land on real slots.
export function timeSelectOptions(ranges, step, is24Hour) {
  const opts = [];
  if (!Array.isArray(ranges)) {
    return opts;
  }
  for (const range of ranges) {
    if (
      !range ||
      typeof range.min !== 'number' ||
      typeof range.max !== 'number'
    ) {
      continue;
    }
    const first = Math.ceil(range.min / step) * step;
    for (let t = first; t <= range.max; t += step) {
      opts.push({ value: t, label: formatTime(t, is24Hour) });
    }
  }
  return opts;
}

// Snap a setpoint to the 0.5 °C grid and clamp it into the zone's [min,max].
export function clampSetpoint(value, min, max) {
  if (!Number.isFinite(value)) {
    return typeof min === 'number' ? min : DEFAULT_SETPOINT;
  }
  let v = Math.round(value / SETPOINT_STEP) * SETPOINT_STEP;
  if (typeof min === 'number' && v < min) {
    v = min;
  }
  if (typeof max === 'number' && v > max) {
    v = max;
  }
  return v;
}

// A day's events as {day, startTime, endTime, setpoint}, endTime being when the
// following segment starts (darwin_core computes it; clone first — it sorts in
// place). Empty schedule → [].
export function getDayEvents(working, day) {
  if (!Array.isArray(working) || working.length === 0) {
    return [];
  }
  return getEventsForDay([...working], day);
}

// --- validation gates -------------------------------------------------------

// darwin_core's addEvent/defaultEndTime assume a fully-populated week (a real
// device schedule always has ≥1 event every day) and throw when a day — or the
// day after it — is empty. Only offer "Add" on a day that already has events, so
// an insert always lands in a populated day and the next-day lookups resolve; a
// genuinely empty day can't be edited from the web yet (it never happens with
// real data). This also keeps a partial/synthetic frame from crashing the editor.
export function canAddToDay(working, day) {
  if (!Array.isArray(working) || working.length === 0 || !day) {
    return false;
  }
  const dayHasEvents = working.some((e) => e.day === day);
  return dayHasEvents && canAdd(working, day);
}

// SI keeps the last event of the day (min 1), so deletion is only offered when a
// day has more than one event.
export function canDeleteFromDay(working, day) {
  if (!Array.isArray(working) || working.length === 0 || !day) {
    return false;
  }
  return canDelete(working, day, false);
}

// --- allowed-time ranges (cloned before delegating) -------------------------

export function addStartRanges(working, day) {
  return availableStartTimeRanges({
    day,
    scheduleEvents: [...working],
    isHpi: false,
  });
}

export function addEndRanges(working, day, startTime) {
  return availableEndTimeRangesAddHaloHeating({
    day,
    scheduleEvents: [...working],
    startTime,
  });
}

export function defaultEndTime(working, day, startTime) {
  try {
    return coreDefaultEndTime([...working], day, startTime);
  } catch {
    return undefined;
  }
}

export function editStartRanges(
  working,
  day,
  endTime,
  existingStart,
  existingEnd,
) {
  return editStartTimeRange(
    [...working],
    day,
    endTime,
    existingStart,
    existingEnd,
    false,
  );
}

export function editEndRanges(
  working,
  day,
  selectedStart,
  existingStart,
  existingEnd,
) {
  return editEndTimeRange(
    [...working],
    day,
    selectedStart,
    existingStart,
    existingEnd,
    false,
  );
}

// --- schedule mutations (return a new flat schedule for the draft) ----------

export function addSiEvent(working, { day, startTime, endTime, setpoint }) {
  const event =
    endTime !== undefined
      ? { day, startTime, endTime, setpoint }
      : { day, startTime, setpoint };
  return addEvent([...working], event, false);
}

export function editSiEvent(working, day, existing, next) {
  const oldEvent = {
    day,
    startTime: existing.startTime,
    endTime: existing.endTime,
    setpoint: existing.setpoint,
  };
  const newEvent = {
    day,
    startTime: next.startTime,
    endTime: next.endTime,
    setpoint: next.setpoint,
  };
  return editEvent([...working], oldEvent, newEvent);
}

export function deleteSiEvent(working, { day, startTime, endTime }) {
  return prepareDeletedSchedule({
    scheduleEvents: [...working],
    day,
    existingStartTime: startTime,
    existingEndTime: endTime,
    isHpi: false,
  });
}

export function dayHasEvents(working, day) {
  return Array.isArray(working) && working.some((e) => e.day === day);
}

// Replace each `toDays` day's events with a copy of `fromDay`'s (darwin_core's
// copySchedule; clone first — it round-trips through unflatten). Returns a new
// flat schedule for the draft. Guards the degenerate inputs copySchedule itself
// throws on (empty schedule, or a source day with no events) — returns the
// schedule unchanged so a partial frame can't crash the editor (the UI already
// gates the Copy button on the source day having events).
export function copyDayToDays(working, fromDay, toDays) {
  if (
    !Array.isArray(working) ||
    working.length === 0 ||
    !dayHasEvents(working, fromDay) ||
    !Array.isArray(toDays) ||
    toDays.length === 0
  ) {
    return working;
  }
  return copySchedule([...working], fromDay, toDays);
}

// --- SI hot water (single on/off segments) ----------------------------------
// Same contiguous model as SI heating, but the value is `on` and the ADD end
// range uses availableEndTimeRanges (not the heating-specific builder).

export function siDhwAddEndRanges(working, day, startTime) {
  return availableEndTimeRanges({
    day,
    scheduleEvents: [...working],
    startTime,
    isHpi: false,
  });
}

export function addSiDhwEvent(working, { day, startTime, endTime, on }) {
  return addEvent([...working], { day, startTime, endTime, on }, false);
}

export function editSiDhwEvent(
  working,
  day,
  existingStartTime,
  { startTime, endTime, on },
) {
  return editEvent(
    [...working],
    { day, startTime: existingStartTime },
    { day, startTime, endTime, on },
  );
}

// --- HPI paired events (comfort/reduced heating, or on/off hot water) --------
// A "primary" event (comfort / on:true) starts the active period; a "secondary"
// (reduced / on:false) starts the following period. The user edits the pair as
// one row spanning primaryStart → secondaryStart. `primaryValue`/`secondaryValue`
// are the darwin_core action fields, e.g. { setpoint: 'comfort' } / { on: true }.

// The primary (comfort / on) rows of a day, each carrying endTime = its paired
// secondary's start (darwin_core computes endTime as the next event's start).
export function pairedRows(working, day, isPrimary) {
  return getDayEvents(working, day).filter(isPrimary);
}

export function pairedAddPrimaryRanges(working, day) {
  return availableStartTimeRanges({
    scheduleEvents: [...working],
    day,
    isHpi: true,
  });
}

export function pairedAddSecondaryRanges(working, day, primaryStart) {
  return availableEndTimeRanges({
    day,
    scheduleEvents: [...working],
    startTime: primaryStart,
    isHpi: true,
  });
}

export function pairedEditPrimaryRange(
  working,
  day,
  existingPrimaryStart,
  secondaryStart,
) {
  return [
    {
      min: minimumComfortTime({
        day,
        scheduleEvents: [...working],
        comfortStartTime: existingPrimaryStart,
      }),
      max: maxiumumComfortTime({ reducedStartTime: secondaryStart }),
    },
  ];
}

export function pairedEditSecondaryRange(
  working,
  day,
  primaryStart,
  existingSecondaryStart,
) {
  return [
    {
      min: minimumReducedTime({ comfortStartTime: primaryStart }),
      max: maximumReducedTime({
        day,
        scheduleEvents: [...working],
        reducedStartTime: existingSecondaryStart,
      }),
    },
  ];
}

export function addPairedEvent(
  working,
  { day, primaryStart, secondaryStart, primaryValue, secondaryValue },
) {
  const nextEventStartTime = defaultEndTime(working, day, primaryStart);
  const primaryEvent = {
    day,
    startTime: primaryStart,
    endTime: secondaryStart,
    ...primaryValue,
  };
  const secondaryEvent = nextEventStartTime
    ? {
        day,
        startTime: secondaryStart,
        endTime: nextEventStartTime,
        ...secondaryValue,
      }
    : { day, startTime: secondaryStart, ...secondaryValue };
  const withPrimary = addEvent([...working], primaryEvent, true);
  return addEvent(withPrimary, secondaryEvent, true);
}

export function editPairedEventSchedule(
  working,
  {
    day,
    existingPrimaryStart,
    existingSecondaryStart,
    primaryStart,
    secondaryStart,
    primaryValue,
    secondaryValue,
  },
) {
  return coreEditPairedEvent(
    [...working],
    { day, startTime: existingPrimaryStart, ...primaryValue },
    { day, startTime: existingSecondaryStart, ...secondaryValue },
    { day, startTime: primaryStart, ...primaryValue },
    { day, startTime: secondaryStart, ...secondaryValue },
  );
}

export function deletePairedEvent(
  working,
  { day, primaryStart, secondaryStart },
) {
  return prepareDeletedSchedule({
    scheduleEvents: [...working],
    day,
    existingStartTime: primaryStart,
    existingEndTime: secondaryStart,
    isHpi: true,
  });
}

export function canDeletePairedDay(working, day) {
  if (!Array.isArray(working) || working.length === 0 || !day) {
    return false;
  }
  return canDelete(working, day, true);
}

// A paired ADD is valid only if the chosen primary/secondary pair doesn't clash
// with or straddle an existing pair — darwin_core's areSelectionsValid (isHpi),
// the same gate the app's AddEventHaloAir* screens use and the web port had been
// missing. Edit mode is constrained by its min/max ranges instead.
export function isValidPairedAdd(working, day, primaryStart, secondaryStart) {
  if (primaryStart === undefined || secondaryStart === undefined) {
    return false;
  }
  try {
    return areSelectionsValid({
      scheduleEvents: [...working],
      day,
      startTime: primaryStart,
      endTime: secondaryStart,
      isHpi: true,
    });
  } catch {
    return false;
  }
}

// A per-kind adapter that hides the single-vs-paired (and heating-vs-hot-water)
// differences behind one uniform interface, so the screen never re-branches on
// `kind`. Each method takes the flat working schedule + a day, and add/edit take
// the editor's payload (single: {startTime,endTime,value}; paired:
// {primaryStart,secondaryStart}). Returns a new flat schedule for the draft.
export function getKindAdapter(kind) {
  const isDhw = kind === 'siDhw' || kind === 'hpiDhw';

  if (isPairedKind(kind)) {
    const cfg = PAIRED_VALUES[kind];
    return {
      kind,
      paired: true,
      isDhw,
      rowsForDay: (working, day) => pairedRows(working, day, cfg.isPrimary),
      canDelete: (working, day) => canDeletePairedDay(working, day),
      // A paired row spans primaryStart → secondaryStart (its pair's start).
      rowToExisting: (row) => ({
        primaryStart: row.startTime,
        secondaryStart: row.endTime,
      }),
      applyAdd: (working, day, { primaryStart, secondaryStart }) =>
        addPairedEvent(working, {
          day,
          primaryStart,
          secondaryStart,
          primaryValue: cfg.primary,
          secondaryValue: cfg.secondary,
        }),
      applyEdit: (working, day, existing, { primaryStart, secondaryStart }) =>
        editPairedEventSchedule(working, {
          day,
          existingPrimaryStart: existing.primaryStart,
          existingSecondaryStart: existing.secondaryStart,
          primaryStart,
          secondaryStart,
          primaryValue: cfg.primary,
          secondaryValue: cfg.secondary,
        }),
      applyDelete: (working, day, existing) =>
        deletePairedEvent(working, {
          day,
          primaryStart: existing.primaryStart,
          secondaryStart: existing.secondaryStart,
        }),
    };
  }

  const addFn = isDhw ? addSiDhwEvent : addSiEvent;
  return {
    kind,
    paired: false,
    isDhw,
    rowsForDay: (working, day) => getDayEvents(working, day),
    canDelete: (working, day) => canDeleteFromDay(working, day),
    rowToExisting: (row) => row,
    applyAdd: (working, day, { startTime, endTime, value }) =>
      addFn(working, { day, startTime, endTime, ...value }),
    applyEdit: (working, day, existing, { startTime, endTime, value }) =>
      isDhw
        ? editSiDhwEvent(working, day, existing.startTime, {
            startTime,
            endTime,
            ...value,
          })
        : editSiEvent(working, day, existing, { startTime, endTime, ...value }),
    applyDelete: (working, day, existing) =>
      deleteSiEvent(working, {
        day,
        startTime: existing.startTime,
        endTime: existing.endTime,
      }),
  };
}

// The darwin_core action fields for each paired kind's primary/secondary events.
export const PAIRED_VALUES = {
  hpiHeating: {
    primary: { setpoint: 'comfort' },
    secondary: { setpoint: 'reduced' },
    isPrimary: (e) => e.setpoint === 'comfort',
  },
  hpiDhw: {
    primary: { on: true },
    secondary: { on: false },
    isPrimary: (e) => e.on === true,
  },
};
